/**
 * The map's camera: which slice of the 1200×800 world is on screen.
 *
 * A camera is a world rectangle (the SVG `viewBox`). Framing a target means: the smallest rectangle with
 * the VIEWPORT's aspect ratio that contains the target plus padding, clamped so it never shows outside the
 * world — except that a viewport taller than the whole world is allowed to show margin, because a phone
 * is taller than the world is. Pure; the renderer only animates between the rectangles this returns.
 *
 * Mirrored on iOS (`MapCamera.swift`) and Android (`MapCamera.kt`).
 */
import { WORLD_H, WORLD_RECT, WORLD_W, type Rect } from './map-layout';

export type ViewBox = Rect;

/** How much of the framed target's size is added around it. */
export const FRAME_PADDING = 0.18;
/** The camera never zooms in past this many world units across (keeps nodes from becoming boulders). */
export const MIN_VIEW_W = 360;
/** …nor out past the whole world — where "the whole world" on a pane WIDER than the world's own 3:2 means
 *  the width that fits the world's full HEIGHT, with margin on the sides (a wide desktop pane must still
 *  show the brief at the top and the buckets at the bottom; cropping them was a real defect). */
export const MAX_VIEW_W = WORLD_W;
export function maxViewWidth(aspect: number): number {
  return Math.max(MAX_VIEW_W, WORLD_H * aspect);
}

export function frame(target: Rect, viewport: { w: number; h: number }, padding = FRAME_PADDING): ViewBox {
  const aspect = viewport.w > 0 && viewport.h > 0 ? viewport.w / viewport.h : WORLD_W / WORLD_H;
  let w = target.w * (1 + padding * 2);
  let h = target.h * (1 + padding * 2);
  // Grow the short side to the viewport's aspect.
  if (w / h < aspect) w = h * aspect;
  else h = w / aspect;
  // Zoom limits are on width; height follows the aspect.
  w = Math.min(Math.max(w, MIN_VIEW_W), maxViewWidth(aspect));
  h = w / aspect;
  const cx = target.x + target.w / 2;
  const cy = target.y + target.h / 2;
  return clamp({ x: cx - w / 2, y: cy - h / 2, w, h });
}

/** The whole world, letterboxed to the viewport's aspect. */
export function frameWorld(viewport: { w: number; h: number }): ViewBox {
  return frame(WORLD_RECT, viewport, 0);
}

/** Keep the camera inside the world on any axis where the world is bigger than the view. */
export function clamp(v: ViewBox): ViewBox {
  const x = v.w >= WORLD_W ? (WORLD_W - v.w) / 2 : Math.min(Math.max(v.x, 0), WORLD_W - v.w);
  const y = v.h >= WORLD_H ? (WORLD_H - v.h) / 2 : Math.min(Math.max(v.y, 0), WORLD_H - v.h);
  return { x, y, w: v.w, h: v.h };
}

/** Zoom by `factor` (>1 in) about a world point, clamped to the zoom limits and the world. */
export function zoomAbout(v: ViewBox, factor: number, at: { x: number; y: number }): ViewBox {
  const w = Math.min(Math.max(v.w / factor, MIN_VIEW_W), maxViewWidth(v.w / Math.max(v.h, 1)));
  const scale = w / v.w;
  const h = v.h * scale;
  return clamp({ x: at.x - (at.x - v.x) * scale, y: at.y - (at.y - v.y) * scale, w, h });
}

/** Pan by a screen delta, given the on-screen size of the view. */
export function pan(v: ViewBox, dxPx: number, dyPx: number, viewport: { w: number; h: number }): ViewBox {
  const unitsPerPx = v.w / Math.max(viewport.w, 1);
  return clamp({ ...v, x: v.x - dxPx * unitsPerPx, y: v.y - dyPx * unitsPerPx });
}

/** A world point from a screen point inside the viewport. */
export function toWorld(v: ViewBox, px: { x: number; y: number }, viewport: { w: number; h: number }): { x: number; y: number } {
  return { x: v.x + (px.x / Math.max(viewport.w, 1)) * v.w, y: v.y + (px.y / Math.max(viewport.h, 1)) * v.h };
}

export function toSvgViewBox(v: ViewBox): string {
  return `${round(v.x)} ${round(v.y)} ${round(v.w)} ${round(v.h)}`;
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * The opening choreography (design, WhatsApp 2026-09-11): show the WHOLE map for a beat so the reader
 * gets its scale, then settle on the brief with its card open. Each step is a camera and how long to hold
 * it before the next transition. The renderer tweens between steps over `transitionMs`.
 */
export interface CameraStep {
  view: ViewBox;
  holdMs: number;
  transitionMs: number;
}

export const ENTRY_WORLD_HOLD_MS = 1600;
export const ENTRY_ZOOM_MS = 900;

export function entrySequence(viewport: { w: number; h: number }, briefRect: Rect): CameraStep[] {
  return [
    { view: frameWorld(viewport), holdMs: ENTRY_WORLD_HOLD_MS, transitionMs: 0 },
    { view: frame(briefRect, viewport), holdMs: 0, transitionMs: ENTRY_ZOOM_MS },
  ];
}

/** Linear blend between two cameras, `t` in [0, 1]. */
export function lerpView(a: ViewBox, b: ViewBox, t: number): ViewBox {
  const k = Math.min(Math.max(t, 0), 1);
  return { x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k, w: a.w + (b.w - a.w) * k, h: a.h + (b.h - a.h) * k };
}

/** Ease-in-out for the tween; native uses the same curve (`easeInOut`). */
export function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}
